const DB_NAME = "justwrite-web-spa";
const DB_VERSION = 1;
const STORE_META = "meta";
const STORE_RECOVERY = "recovery";
const STORE_BOOKS = "books";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_META)) db.createObjectStore(STORE_META);
      if (!db.objectStoreNames.contains(STORE_RECOVERY)) db.createObjectStore(STORE_RECOVERY);
      if (!db.objectStoreNames.contains(STORE_BOOKS)) db.createObjectStore(STORE_BOOKS);
    };
    req.onsuccess = () => resolve(req.result);
  });
}

async function idbGet<T>(store: string, key: string): Promise<T | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readonly");
    const req = tx.objectStore(store).get(key);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve((req.result as T | undefined) ?? null);
  });
}

async function idbSet(store: string, key: string, value: unknown): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    const req = tx.objectStore(store).put(value, key);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve();
  });
}

async function idbDel(store: string, key: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(store, "readwrite");
    const req = tx.objectStore(store).delete(key);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve();
  });
}

const MACHINE_ID_KEY = "machineId";
const LAST_SESSION_KEY = "lastSession";
const RECOVERY_KEY = "current";

export async function getOrCreateWebMachineId(): Promise<{ machineId: string; machineName: string }> {
  let machineId = await idbGet<string>(STORE_META, MACHINE_ID_KEY);
  if (!machineId?.trim()) {
    machineId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `web-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    await idbSet(STORE_META, MACHINE_ID_KEY, machineId);
  }
  return { machineId, machineName: "Web browser" };
}

export type WebLastSession = {
  filePath: string;
  bookId?: string;
  updatedAt: string;
};

export async function readWebLastSession(): Promise<WebLastSession | null> {
  return idbGet<WebLastSession>(STORE_META, LAST_SESSION_KEY);
}

export async function writeWebLastSession(session: WebLastSession | null): Promise<void> {
  if (!session) {
    await idbDel(STORE_META, LAST_SESSION_KEY);
    return;
  }
  await idbSet(STORE_META, LAST_SESSION_KEY, session);
}

export async function loadWebRecovery(): Promise<unknown | null> {
  return idbGet(STORE_RECOVERY, RECOVERY_KEY);
}

export async function saveWebRecovery(payload: unknown): Promise<void> {
  await idbSet(STORE_RECOVERY, RECOVERY_KEY, payload);
}

export async function clearWebRecovery(): Promise<void> {
  await idbDel(STORE_RECOVERY, RECOVERY_KEY);
}

/** Virtual cloud path used instead of a local .sbk file on web. */
export function cloudVirtualPath(bookId: string): string {
  return `cloud://${bookId.trim()}`;
}

export function isCloudVirtualPath(filePath: string | null | undefined): boolean {
  return Boolean(filePath?.startsWith("cloud://"));
}

export async function cacheWebBookDraft(bookId: string, book: unknown): Promise<void> {
  const id = bookId.trim();
  if (!id) return;
  await idbSet(STORE_BOOKS, id, { book, updatedAt: new Date().toISOString() });
}

export async function readWebBookDraft(bookId: string): Promise<unknown | null> {
  const row = await idbGet<{ book: unknown }>(STORE_BOOKS, bookId.trim());
  return row?.book ?? null;
}

const BOOKSHELF_CACHE_KEY = "bookshelfSummaries";

export type CachedBookshelfEntry = {
  id: string;
  title: string;
  author: string;
  updatedAt: string;
  chapterCount: number;
  pendingSync?: boolean;
};

export async function cacheBookshelfSummaries(rows: CachedBookshelfEntry[]): Promise<void> {
  await idbSet(STORE_META, BOOKSHELF_CACHE_KEY, rows);
}

export async function readBookshelfSummaries(): Promise<CachedBookshelfEntry[] | null> {
  return idbGet<CachedBookshelfEntry[]>(STORE_META, BOOKSHELF_CACHE_KEY);
}

export async function upsertBookshelfSummary(entry: CachedBookshelfEntry): Promise<void> {
  const current = (await readBookshelfSummaries()) ?? [];
  const next = [entry, ...current.filter((row) => row.id !== entry.id)];
  await cacheBookshelfSummaries(next);
}
